// @flow
import * as React from 'react';
import { memoize } from 'lodash';
import L from 'leaflet';
import { Box, ThemeContext } from 'grommet';
import type { LatLng } from 'react-leaflet/lib/types';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import { withConfirmation, withRouter } from 'speed-js-react';
import type { Resource, Map } from 'esm-web-pro/containers/map/types';
import NetworkMapEditor, { CONTENT_TOOLTIP_PANE_NAME } from 'esm-web-pro/containers/pages/network/NetworkMapEditor';
import { getAddressTitleAdditionalOptions } from 'esm-web-pro/containers/pages/coverage/Util';
import { MapContext } from 'esm-web-pro/containers/map/context';
import AddressLayer from 'esm-web-pro/components/maps/leaflet_/AddressLayer';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import attributeMessages from 'esm-web-pro/messages/attributeMessages';
import { POINTS_WITH_CABLE_RESERVE_DISPLAY } from 'esm-web-pro/redux/networks/constants';
import { pointIcon } from './Util';
import NetworkCreator from './NetworkCreator';

export const MAP_INITIAL_ZOOM = 18;

const geometryType = {
    polygon: 'Polygon',
    polyline: 'Polyline',
    point: 'Point',
    marker: 'Marker',
};

type Props = {
    map: Map,
    setPosition: (center: LatLng, zoom: number) => {},
    setMapLoading: (mapLoading: boolean) => void,
    zoom: number,
    center: [number, number],
    intl: IntlShape,
    history: { push: (location: string) => void },
    match: Object,
}

const messages = defineMessages({
    'networks-map-route': {
        id: 'networks-map-route',
        defaultMessage: 'Route',
    },
    'networks-map-route-plural': {
        id: 'networks-map-route-plural',
        defaultMessage: 'Routes',
    },
    'networks-map-point': {
        id: 'networks-map-point',
        defaultMessage: 'Route Point',
    },
    'networks-map-points-plural': {
        id: 'networks-map-points-plural',
        defaultMessage: 'Route Points',
    },
    'networks-map-points-object_type': {
        id: 'networks-map-points-object_type',
        defaultMessage: 'Object type',
    },
    'networks-map-points-model': {
        id: 'networks-map-points-model',
        defaultMessage: 'Model',
    },
    'networks-map-points-alias': {
        id: 'networks-map-points-alias',
        defaultMessage: 'Alias',
    },
    'networks-map-points-infrastructure-provider': {
        id: 'networks-map-infrastructure-provider',
        defaultMessage: 'Infrastructure provider',
    },
    'networks-map-points-underground-connection': {
        id: 'networks-map-underground-connection',
        defaultMessage: 'Underground connection',
    },
    'networks-map-points-provider-ref': {
        id: 'networks-map-provider-ref',
        defaultMessage: 'Provider ref',
    },
    'networks-map-points-shaft': {
        id: 'networks-map-points-shaft',
        defaultMessage: 'Shaft',
    },
    'networks-map-points-pillar': {
        id: 'networks-map-points-pillar',
        defaultMessage: 'Pillar',
    },
    'networks-map-points-cabinet': {
        id: 'networks-map-points-cabinet',
        defaultMessage: 'Cabinet',
    },
    'networks-map-points-wallpoint': {
        id: 'networks-map-points-wallpoint',
        defaultMessage: 'Wall point',
    },
    'networks-map-points-gispoint': {
        id: 'networks-map-points-undefinedpoint',
        defaultMessage: 'Undefined point',
    },
    'networks-map-points-pop': {
        id: 'networks-map-points-pop',
        defaultMessage: 'PoP',
    },
    'networks-map-points-linecut': {
        id: 'networks-map-points-linecut',
        defaultMessage: 'Line Cut',
    },
    'networks-map-points-fork': {
        id: 'networks-map-points-fork',
        defaultMessage: 'Fork',
    },
    'networks-map-points-is-shared': {
        id: 'is-shared-attribute',
        defaultMessage: 'Is shared',
    },
    'networks-map-points-export': {
        id: 'export-attribute',
        defaultMessage: 'Export',
    },
});

const COLOR = {
    green: '#0074d9',
    darkerGreen: '#809c17',
    red: '#d02120',
    blue: '#0000FF',
    grey: '#606060',
};

const SHAFT_OBJECT_TYPE = 'shaft';
export const CONTENT_BOXES_MIN_ZOOM = 17;
export const POINTS_MIN_ZOOM = 17;


@withConfirmation
class NetworkTab extends React.Component<Props> {
    resources: Array<Resource>;

    ref: null | Object;

    constructor(props) {
        super(props);
        this.ref = React.createRef();

        this.getResources = memoize(this.getResources);

        this.state = {
            showMapObjects: {
                regions: {
                    name: 'gisRegions.geo',
                    show: false,
                },
                points: {
                    name: 'gisPoint.geo',
                    show: true,
                },
                lines: {
                    name: 'gisLine.geo',
                    show: true,
                },
            },
        };
    }

    getQuery = (objectType: String) => {
        const pointFilters = window.sessionStorage.getItem('pointFilters');
        const lineFilters = window.sessionStorage.getItem('lineFilters');
        let filters = null;
        if (objectType === 'points') {
            if (pointFilters) {
                filters = JSON.parse(pointFilters);
            }
        }
        if (objectType === 'lines') {
            if (lineFilters) {
                filters = JSON.parse(lineFilters);
            }
        }
        return filters;
    };

    getResources = (mapUUID, map) => {
        const resources = [];
        if (mapUUID && map) {
            resources.push(
                {
                    name: ['address.map', 'addresses'],
                    sidePaneResource: 'address',
                    key: 'not-changed',
                    arguments: [map.uuid],
                    label: messages['addresses-address-btn'],
                    labelPlural: messages['addresses-address-btn-plural'],
                    type: 'point',
                    geometryField: 'geom',
                    query: {
                        geojson: true,
                        limit: 1000,
                    },
                    allowNew: false,
                    allowEdit: false,
                    allowDelete: false,
                    minZoom: 19,
                    additionalOptions: (feature) => getAddressTitleAdditionalOptions(feature.properties),
                    pointToLayer: (feature, latlng) => new AddressLayer(latlng),
                },
            );

            resources.push(
                {
                    name: 'gisLine.geo',
                    arguments: [mapUUID],
                    label: messages['networks-map-route'],
                    labelPlural: messages['networks-map-route-plural'],
                    type: 'polyline',
                    geometryField: 'path',
                    query: () => {
                        const filters = this.getQuery('lines');
                        return {
                            ...filters,
                            geojson: true,
                        };
                    },
                    displayFields: ['type'],
                    // allowNew: true,
                    allowEdit: true,
                    allowDelete: true,
                    additionalOptions: this.getLineAdditionalOptions,
                    tooltipContent: this.getLineTooltipContent,
                    navigateTo: this.navigateToLine,
                    onAddToMap: this.onAddLineToMap,
                    onRemove: this.onRemoveLine,
                },
            );
            resources.push(
                {
                    name: 'gisPoint.geo',
                    arguments: [mapUUID],
                    label: messages['networks-map-point'],
                    labelPlural: messages['networks-map-points-plural'],
                    type: 'point',
                    geometryField: 'coordinates',
                    query: () => {
                        const filters = this.getQuery('points');
                        return {
                            ...filters,
                            geojson: true,
                        };
                    },
                    displayFields: ['type'],
                    // allowNew: true,
                    // minZoom: !map.is_draft ? 18 : undefined,
                    key: 'map-networks',
                    allowEdit: true,
                    allowDelete: true,
                    additionalOptions: this.getPointAdditionalOptions,
                    tooltipContent: this.getPointTooltipContent,
                    navigateTo: this.navigateToPoint,
                    minZoom: POINTS_MIN_ZOOM,
                    onAddToMap: () => {
                    },
                },
            );
            resources.push(
                {
                    name: 'gisRegions.geo',
                    arguments: [mapUUID],
                    type: 'polygon',
                    geometryField: 'coordinates',
                    query: {
                        geojson: true,
                    },
                    displayFields: ['name'],
                    key: 'map-networks',
                    allowEdit: true,
                    allowDelete: true,
                    additionalOptions: (feature) => this.getPolygonAdditionalOptions(map, feature),
                    navigateTo: this.navigateToRegion,
                },
            );
        }
        return resources;
    };

    toggleShowMapObjects = (object) => {
        const { showMapObjects } = this.state;
        this.setState(
            {
                showMapObjects: {
                    ...showMapObjects,
                    [object]: {
                        name: showMapObjects[object].name,
                        show: !showMapObjects[object].show,
                    },
                },
            },
        );
    }

    getPolygonAdditionalOptions = (map: Object = null, feature: Object) => {
        const options: Object = {};

        const coverage = feature.properties;
        if (!coverage.valid_from && coverage.valid_to) {
            options.color = COLOR.red;
            if (map) {
                const versionCreateDate = new Date(map.version_create_date);
                const activeMapValidFrom = new Date(feature.properties.modified);
                if (versionCreateDate < activeMapValidFrom) {
                    options.fillOpacity = 0.7;
                    options.className = 'deleted-coverage';
                }
            }
        } else if (!coverage.valid_from) {
            options.color = COLOR.darkerGreen;
            options.fillOpacity = 0.5;
            options.fillColor = COLOR.darkerGreen;
            options.className = 'draft-coverage';
        } else {
            options.fillOpacity = 0.2;
            options.color = COLOR.green;
            options.fillColor = null;
            options.className = 'active-coverage';
        }
        return options;
    };

    getPointAdditionalOptions = (feature: Object, layer: Object) => {
        const { properties: { point } } = feature;
        const { zoom } = this.props;
        const options: Object = {};
        let cableReservesCount;

        const contentCount = zoom >= CONTENT_BOXES_MIN_ZOOM ? point && point.content_count : null;

        if (
            point
            && point.child
            && POINTS_WITH_CABLE_RESERVE_DISPLAY.includes(point.child.type)
        ) {
            cableReservesCount = zoom >= CONTENT_BOXES_MIN_ZOOM && point.cable_reserves;
        }

        const icon = point && point.child
            ? pointIcon(
                point?.child?.type,
                false,
                point.color,
                point.child.underground_connection,
                contentCount,
                cableReservesCount,
            )
            : pointIcon(point);
        if (icon) {
            options.icon = icon;
        }
        return options;
    };

    _createLineTooltip = (layer, contentCount, map) => L.tooltip({
        permanent: true,
        direction: 'center',
        className: 'content-tooltip-line',
        opacity: 1,
        pane: CONTENT_TOOLTIP_PANE_NAME,
    }).setContent(contentCount.toString())
        .setLatLng(layer.getCenter())
        .addTo(map.leafletElement);

    onAddLineToMap = (layer, map) => {
        const { cables_count: cablesCount, pipes_count: pipesCount } = layer.feature.properties;
        const { zoom } = this.props;
        if ((cablesCount || pipesCount) && zoom >= CONTENT_BOXES_MIN_ZOOM) {
            if (layer.contentTooltip) {
                const prevContent = layer.contentTooltip.getContent();
                if (prevContent !== `${pipesCount}/${cablesCount}`) {
                    map.leafletElement.removeLayer(layer.contentTooltip);
                    layer.contentTooltip = this._createLineTooltip(layer, `${pipesCount}/${cablesCount}`, map);
                }
            } else {
                layer.contentTooltip = this._createLineTooltip(layer, `${pipesCount}/${cablesCount}`, map);
            }
        } else if (layer.contentTooltip) {
            map.leafletElement.removeLayer(layer.contentTooltip);
            layer.contentTooltip = null;
        }
    };

    onRemoveLine = (layer, map) => {
        if (layer.contentTooltip) {
            map.leafletElement.removeLayer(layer.contentTooltip);
        }
    };

    getLineAdditionalOptions = (feature: Object, layer: Object) => {
        const { color, cables_count: cablesCount } = feature.properties;

        let dashArray = null;
        if (cablesCount === 0) {
            dashArray = [10, 5];
        }

        return { color, dashArray };
    };


    getLineTooltipContent = (feature): string => {
        const { intl: { formatMessage } } = this.props;
        const { properties: { content_data: contentData } } = feature;

        let content = '';
        if (Object.keys(contentData).length > 0) {
            content += `<b>${formatMessage(cableAndFiberMessages.cablePlural)}:</b> <br/>`;
            Object.keys(contentData).forEach((key) => {
                content += `&emsp;${key}: ${contentData[key]} <br/>`;
            });
        }
        return content;
    };

    getPointTooltipContent = (feature) => {
        const { intl: { formatMessage } } = this.props;

        const {
            point, infrastructureProvider, alias, sequence,
        } = feature.properties;

        let model;
        let ossName;
        let type;
        let providerRef;
        let export_;
        let contentData = {};
        let undergroundConnection;

        if (point && point.child) {
            ({
                child: { model, underground_connection: undergroundConnection, type },
                content_data: contentData, provider_ref: providerRef, oss_name: ossName,
                export: export_,
            } = point);
        }

        let content = '';

        content += alias ? `<b>${formatMessage(messages['networks-map-points-alias'])}:</b> ${alias} <br/>` : '';
        content += type
            ? `<b>${formatMessage(messages['networks-map-points-object_type'])}:</b> ${
                formatMessage(messages[`networks-map-points-${type}`])} ${sequence} <br/>` : '';
        content += model ? `<b>${formatMessage(messages['networks-map-points-model'])}:</b> ${model.name} <br/>` : '';
        content += infrastructureProvider
            ? `<b>${formatMessage(messages['networks-map-points-infrastructure-provider'])}:</b>
                ${infrastructureProvider} <br/>` : '';
        if (type && type === 'pop') {
            content += ossName ? `<b>${formatMessage(attributeMessages.ossName)}:</b>
                ${ossName} <br/>` : '';
        }
        content += undergroundConnection
            ? `<b>${formatMessage(messages['networks-map-points-underground-connection'])}:</b> 
           ${formatMessage({ id: 'yes' })} <br/>` : '';
        content += providerRef ? `<b>${formatMessage(messages['networks-map-points-provider-ref'])}:</b>
                ${providerRef} <br/>` : '';

        if (type === SHAFT_OBJECT_TYPE) {
            const { child: { is_shared: isShared } } = point;
            content += isShared
                ? `<b>${formatMessage(messages['networks-map-points-is-shared'])}:</b> 
           ${formatMessage({ id: 'yes' })} <br/>` : '';
        }

        if (typeof export_ !== 'undefined') {
            content += `<b>${formatMessage(messages['networks-map-points-export'])}:</b> 
           ${formatMessage({ id: export_ ? 'yes' : 'no' })} <br/>`;
        }

        Object.keys(contentData).forEach((key) => {
            content += `<b>${formatMessage(point12Messages[`${key}Plural`])}:</b> <br/>`;

            Object.keys(contentData[key]).forEach((point12model) => {
                content += `&emsp;${point12model}: ${contentData[key][point12model]} <br/>`;
            });
        });

        return content;
    };

    navigateToPoint = (layer, result) => {
        const { point, identity } = layer.feature.properties;
        this.navigateTo(point?.child?.type || 'gispoint', point?.child?.identity || identity);
    };

    navigateToLine = (layer, result) => {
        this.navigateTo('gisline', layer.feature.properties.identity);
    };

    navigateToRegion = (layer, result) => {
        this.navigateTo('region', layer.feature.id);
    };

    navigateTo = (type, uuid) => {
        const { history, match } = this.props;
        history.push(`${match.url}/layer/${type}/${uuid}/`);
    };

    render() {
        const {
            map, setPosition, center, zoom, setMapLoading,
        } = this.props;

        return (
            <ThemeContext.Extend
                value={{ box: { extend: { position: 'relative', 'box-shadow': 'none' } } }}
            >
                <Box height="100%">
                    <MapContext.Provider value={map}>
                        <NetworkMapEditor
                            forwarded={this.ref}
                            zoom={zoom || MAP_INITIAL_ZOOM}
                            center={
                                center || L.polygon(
                                    map.bounds.coordinates[0].map((c) => [c[1], c[0]]),
                                ).getBounds().getCenter()
                            }
                            setPosition={setPosition}
                            setMapLoading={setMapLoading}
                            bounds={map.bounds.coordinates[0]}
                            resources={this.getResources(map.uuid, map)}
                            allowCreate={map.is_draft}
                            allowMeasure={map.is_draft}
                            toggleShowMapObjects={this.toggleShowMapObjects}
                            showMapObjects={this.state.showMapObjects}
                            creator={NetworkCreator}
                            currentMap={map}
                        />
                    </MapContext.Provider>
                </Box>
            </ThemeContext.Extend>
        );
    }
}

export { MapContext };
export default withRouter(injectIntl(NetworkTab));
